import type { Styles } from '@adornis/ass/style.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { subscribeMyTerminsStartingFrom } from '@ils/evaluation-scheduler/api/EvaluationTermin.js';
import { subscribeFinalRatingDocForSlot } from '@ils/ratings/api/ratings.js';
import { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, switchMap } from 'rxjs';

@customElement('ils-evaluation-termin')
export class ILSEvaluationTermin extends ChemistryLitElement {
  @property({ attribute: false }) terminID = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly _termins = new RXController(
    this,
    this.terminID.observable.pipe(
      filter(Boolean),
      switchMap(terminID => subscribeMyTerminsStartingFrom(terminID)({ _id: 1, slotIDs: 1 })),
    ),
  );

  @state() private readonly _slotID = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly _finalRating = new RXController(
    this,
    this._slotID.observable.pipe(
      filter(Boolean),
      switchMap(slotID => subscribeFinalRatingDocForSlot(slotID)(ExamSlotRatingDoc.allFields)),
    ),
  );

  override connectedCallback(): void {
    super.connectedCallback();

    this._termins.observable.pipe(filter(Boolean)).subscribe(termins => {
      const termin = termins[0];
      if (termin && !this._slotID.value) this._slotID.next(termin.slotIDs[0]);
    });

    this._finalRating.observable.pipe(filter(Boolean)).subscribe(entity => {
      if (!entity.committed) return;
      const currentSlotID = this._slotID.value;
      if (!currentSlotID || !this._termins.value?.length) return;

      const slotIDs = this._termins.value.flatMap(termin => termin.slotIDs);

      const currentSlotIndex = slotIDs.findIndex(id => id === currentSlotID);
      const nextSlotID = slotIDs.at(currentSlotIndex + 1);
      if (!nextSlotID) this._slotID.next(null);
      else this._slotID.next(nextSlotID);
    });
  }

  override render() {
    return html` <ils-evaluation-sync .slotID=${this._slotID.value}></ils-evaluation-sync> `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    ];
  }
}
